import React, { useState } from 'react';
import axios from 'axios';
import { FlaskConical, Copy, CheckCircle2, XCircle, AlertTriangle, ShieldAlert } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * TT-CREATOMATE-DIAGNOSTIC-UI-009
 * Admin-only diagnostic panel — runs the full Creatomate A/B suite from a single click:
 *  1) GET  /api/admin/creatomate-version
 *  2) GET  /api/admin/products/{id}/creatomate-video-status
 *  3) POST /api/admin/products/{id}/creatomate-diagnostic-render?mode=source_wrapper
 *  4) POST /api/admin/products/{id}/creatomate-diagnostic-render?mode=flat
 *
 * NEVER writes playbackUrl/thumbnailUrl. NEVER publishes. NEVER triggers Bunny bridge.
 * Diagnostic-only. Copy-to-clipboard for JSON export.
 * Strictly gated to admin/super_admin roles.
 */
const CreatomateDiagnosticPanel = ({ productId }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);

  // Admin-only gate — public and vendor users see nothing.
  const isAdminLike = user && (user.role === 'admin' || user.role === 'super_admin');
  if (!isAdminLike) return null;

  const runSuite = async () => {
    if (!productId) return;
    setBusy(true);
    setError('');
    setResults(null);
    setCopied(false);

    const suite = {
      runAt: new Date().toISOString(),
      productId,
      version: null,
      currentStatus: null,
      sourceWrapper: null,
      flat: null,
      errors: {},
    };

    // Independent calls, each isolated with its own try/catch so a single failure
    // does not abort the suite.
    const safe = async (label, fn) => {
      try { return await fn(); }
      catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Bilinmeyen hata';
        suite.errors[label] = detail;
        return null;
      }
    };

    suite.version = await safe('version', async () => {
      const r = await axios.get(`${API_URL}/api/admin/creatomate-version`);
      return r.data;
    });
    suite.currentStatus = await safe('currentStatus', async () => {
      const r = await axios.get(`${API_URL}/api/admin/products/${productId}/creatomate-video-status`);
      return r.data;
    });
    suite.sourceWrapper = await safe('sourceWrapper', async () => {
      const r = await axios.post(
        `${API_URL}/api/admin/products/${productId}/creatomate-diagnostic-render?mode=source_wrapper`,
      );
      return r.data;
    });
    suite.flat = await safe('flat', async () => {
      const r = await axios.post(
        `${API_URL}/api/admin/products/${productId}/creatomate-diagnostic-render?mode=flat`,
      );
      return r.data;
    });

    setResults(suite);
    setBusy(false);
  };

  const copyJson = async () => {
    if (!results) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback — select-all trick
      const ta = document.createElement('textarea');
      ta.value = JSON.stringify(results, null, 2);
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* silent */ }
      document.body.removeChild(ta);
    }
  };

  // Classify a single mode result → one of: mp4 | image | failed | invalid_output | pending | not_run
  const classify = (block) => {
    if (!block) return 'not_run';
    if (block.ok === false) return 'failed';
    const d = block.diagnostics || {};
    if (d.creatomateResponseStatus && d.creatomateResponseStatus >= 400) return 'failed';
    if (d.isValidMp4 === false) return 'image';
    if (d.invalidReason && d.invalidReason.startsWith('unexpected_resolution')) return 'invalid_output';
    if (d.isValidMp4 === true) return 'mp4';
    if (d.renderStatus === 'planned' || d.renderStatus === 'processing' || d.renderStatus === 'rendering') return 'pending';
    return 'pending';
  };

  const swClass = results ? classify(results.sourceWrapper) : null;
  const flatClass = results ? classify(results.flat) : null;

  const recommendation = (() => {
    if (!results) return null;
    const wins = (c) => c === 'mp4' || c === 'pending';
    if (wins(swClass) && !wins(flatClass)) return { mode: 'source_wrapper', reason: 'source_wrapper geçerli render başlattı; flat başarısız.' };
    if (wins(flatClass) && !wins(swClass)) return { mode: 'flat', reason: 'flat geçerli render başlattı; source_wrapper başarısız.' };
    if (wins(swClass) && wins(flatClass)) return { mode: 'source_wrapper', reason: 'Her iki mod da render başlattı. source_wrapper Creatomate v2 için tavsiye edilen kabuktur.' };
    return { mode: null, reason: 'Her iki mod da geçerli MP4 üretmedi. Diagnostic çıktısını Creatomate destek ekibiyle paylaşın.' };
  })();

  const apiKeyMissing = results?.version && results.version.creatomateApiKeyConfigured === false;

  return (
    <div className="mt-4 pt-4 border-t border-dashed border-purple-300" data-testid="creatomate-diagnostic-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <FlaskConical className="w-4 h-4" style={{ color: '#7C3AED' }} />
        <p className="text-xs font-black uppercase tracking-wider">Creatomate Tanı Testi (Admin)</p>
        <span className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest bg-purple-100 text-purple-800">
          Diagnostic-only
        </span>
      </div>
      <p className="text-[11px] mb-2" style={{ color: 'var(--text-secondary)' }}>
        Bu test <strong>gerçek ürün verisi</strong> ile source_wrapper ve flat payload modlarını A/B karşılaştırır.
        Sonuçlar <strong>playbackUrl / thumbnailUrl yazmaz</strong>, ürünü yayına almaz, Bunny yüklemesi tetiklemez.
        Yalnızca yönetici görebilir.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={!productId || busy}
          onClick={runSuite}
          className={`text-xs font-black uppercase tracking-wider px-4 py-2 rounded border-2 transition ${
            (!productId || busy)
              ? 'border-neutral-300 text-neutral-400 cursor-not-allowed'
              : 'border-purple-700 text-purple-800 hover:bg-purple-700 hover:text-white'
          }`}
          data-testid="creatomate-diagnostic-run-button"
        >
          {busy ? 'Çalıştırılıyor…' : 'Creatomate Tanı Testi Çalıştır'}
        </button>
        {results && (
          <button
            type="button"
            onClick={copyJson}
            className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border border-neutral-400 hover:bg-neutral-100 flex items-center gap-1.5"
            data-testid="creatomate-diagnostic-copy-button"
          >
            <Copy className="w-3.5 h-3.5" />
            {copied ? 'Kopyalandı!' : 'JSON Kopyala'}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-2 text-xs font-semibold text-red-700" data-testid="creatomate-diagnostic-error">{error}</p>
      )}

      {results && (
        <div className="mt-3 space-y-3" data-testid="creatomate-diagnostic-results">
          {apiKeyMissing && (
            <div className="p-2 rounded bg-amber-50 border border-amber-300 flex items-start gap-2" data-testid="creatomate-diagnostic-key-missing">
              <ShieldAlert className="w-4 h-4 text-amber-800 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 font-bold">
                Production CREATOMATE_API_KEY yapılandırılmamış veya placeholder. Render istekleri gönderilemedi; yalnızca payload yapısı analiz edildi.
              </p>
            </div>
          )}

          {/* Version block */}
          <VersionBlock data={results.version} err={results.errors.version} />

          {/* Current status snapshot */}
          <StatusBlock data={results.currentStatus} err={results.errors.currentStatus} />

          {/* A/B render blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ModeBlock label="source_wrapper" data={results.sourceWrapper} err={results.errors.sourceWrapper} klass={swClass} />
            <ModeBlock label="flat" data={results.flat} err={results.errors.flat} klass={flatClass} />
          </div>

          {/* Recommendation */}
          {recommendation && (
            <div
              className={`p-3 rounded border-2 ${recommendation.mode ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-400'}`}
              data-testid="creatomate-diagnostic-recommendation"
            >
              <p className="text-xs font-black uppercase tracking-wider mb-1"
                style={{ color: recommendation.mode ? '#065F46' : '#991B1B' }}>
                Önerilen Payload Mode
              </p>
              <p className="text-sm font-black font-mono" data-testid="creatomate-diagnostic-recommended-mode"
                style={{ color: recommendation.mode ? '#065F46' : '#991B1B' }}>
                {recommendation.mode || 'BELİRSİZ — inceleme gerekli'}
              </p>
              <p className="text-xs mt-1" style={{ color: recommendation.mode ? '#065F46' : '#991B1B' }}>
                {recommendation.reason}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const KV = ({ k, v }) => {
  const rendered = v === true ? 'true'
    : v === false ? 'false'
    : v === null || v === undefined ? '—'
    : Array.isArray(v) ? (v.length ? v.join(', ') : '[]')
    : typeof v === 'object' ? JSON.stringify(v)
    : String(v);
  return (
    <div className="flex justify-between gap-2 py-0.5 border-b border-dashed border-neutral-200 last:border-b-0">
      <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-600 flex-shrink-0">{k}</span>
      <span className="text-[11px] font-mono text-right break-all" style={{ color: 'var(--text-primary)' }}>{rendered}</span>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <div className="border border-neutral-300 rounded p-2 bg-neutral-50" data-testid={testid}>
    <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>{title}</p>
    <div>{children}</div>
  </div>
);

const VersionBlock = ({ data, err }) => (
  <Section title="Integration Version" testid="creatomate-diagnostic-version-block">
    {err ? <p className="text-xs text-red-700 font-semibold">Hata detayı: {err}</p> : (
      <>
        <KV k="integrationVersion" v={data?.integrationVersion} />
        <KV k="defaultPayloadMode" v={data?.defaultPayloadMode} />
        <KV k="creatomateApiKeyConfigured" v={data?.creatomateApiKeyConfigured} />
        <KV k="ttsProviderConfigured" v={data?.ttsProviderConfigured} />
      </>
    )}
  </Section>
);

const StatusBlock = ({ data, err }) => (
  <Section title="Current Video Status Snapshot" testid="creatomate-diagnostic-status-block">
    {err ? <p className="text-xs text-red-700 font-semibold">Hata detayı: {err}</p> : (
      <>
        <KV k="creatomateStatus" v={data?.creatomateStatus} />
        <KV k="creatomateOutputInvalid" v={data?.creatomateOutputInvalid} />
        <KV k="creatomateVoiceoverStatus" v={data?.creatomateVoiceoverStatus} />
        <KV k="creatomateAttemptNumber" v={data?.creatomateAttemptNumber} />
        <KV k="retryAllowed" v={data?.creatomateStatus === 'failed' || data?.creatomateStatus === 'invalid_output' || !data?.creatomateStatus} />
        <KV k="detectedWidth" v={data?.creatomateDetectedWidth} />
        <KV k="detectedHeight" v={data?.creatomateDetectedHeight} />
        <KV k="detectedDuration" v={data?.creatomateDetectedDuration} />
        <KV k="safeErrorMessage" v={data?.creatomateErrorMessage} />
        {data?.creatomateDiagnostics && (
          <div className="mt-2 pt-1 border-t border-neutral-300">
            <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>Cached diagnostics snapshot</p>
            {Object.entries(data.creatomateDiagnostics).map(([k, v]) => <KV key={k} k={k} v={v} />)}
          </div>
        )}
      </>
    )}
  </Section>
);

const CLASS_STYLE = {
  mp4:             { bg: 'bg-emerald-50', border: 'border-emerald-400', color: '#065F46', label: 'GEÇERLİ MP4', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  pending:         { bg: 'bg-sky-50',     border: 'border-sky-400',     color: '#0C4A6E', label: 'RENDER BAŞLADI (beklemede)', icon: <FlaskConical className="w-3.5 h-3.5" /> },
  image:           { bg: 'bg-red-50',     border: 'border-red-400',     color: '#7F1D1D', label: 'ÇIKTI GÖRSEL (MP4 DEĞİL)', icon: <XCircle className="w-3.5 h-3.5" /> },
  invalid_output:  { bg: 'bg-red-50',     border: 'border-red-400',     color: '#7F1D1D', label: 'ÇÖZÜNÜRLÜK HATASI', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  failed:          { bg: 'bg-red-50',     border: 'border-red-400',     color: '#7F1D1D', label: 'BAŞARISIZ', icon: <XCircle className="w-3.5 h-3.5" /> },
  not_run:         { bg: 'bg-neutral-50', border: 'border-neutral-300', color: '#404040', label: 'ÇALIŞTIRILMADI', icon: null },
};

const ModeBlock = ({ label, data, err, klass }) => {
  const style = CLASS_STYLE[klass] || CLASS_STYLE.not_run;
  const d = data?.diagnostics || {};
  return (
    <div className={`rounded border-2 p-2 ${style.bg} ${style.border}`} data-testid={`creatomate-diagnostic-mode-${label}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-black uppercase tracking-wider font-mono">{label}</span>
        <span
          className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest flex items-center gap-1"
          style={{ background: style.color, color: '#FFF' }}
          data-testid={`creatomate-diagnostic-mode-${label}-verdict`}
        >
          {style.icon}{style.label}
        </span>
      </div>
      {err && <p className="text-xs text-red-700 font-semibold mb-1">Hata detayı: {err}</p>}
      {data && (
        <>
          <KV k="ok" v={data.ok} />
          <KV k="payloadMode" v={data.payloadMode ?? d.payloadMode} />
          <KV k="requestTopLevelKeys" v={d.requestTopLevelKeys} />
          <KV k="hasSourceWrapper" v={d.hasSourceWrapper} />
          <KV k="sourceKeys" v={d.sourceKeys} />
          <KV k="sourceWidth" v={d.sourceWidth} />
          <KV k="sourceHeight" v={d.sourceHeight} />
          <KV k="sourceDuration" v={d.sourceDuration} />
          <KV k="sourceFillColorExists" v={d.sourceFillColorExists} />
          <KV k="sourceBackgroundColorExists" v={d.sourceBackgroundColorExists} />
          <KV k="elementCount" v={d.elementCount} />
          <KV k="elementTypes" v={d.elementTypes} />
          <KV k="hasImageElements" v={d.hasImageElements} />
          <KV k="hasTextElements" v={d.hasTextElements} />
          <KV k="hasLogoElement" v={d.hasLogoElement} />
          <KV k="isExpectedResolution" v={d.isExpectedResolution} />
          {/* TT-CREATOMATE-FIX-INVALID-ELEMENT-TYPE-010: element validation results */}
          <KV k="elementTypeValidationPassed" v={d.elementTypeValidationPassed} />
          <KV k="normalizedElementTypes" v={d.normalizedElementTypes} />
          <KV k="unsupportedElementTypes" v={d.unsupportedElementTypes} />
          <KV k="invalidElementIndex" v={d.invalidElementIndex} />
          <KV k="invalidElementType" v={d.invalidElementType} />
          <KV k="invalidElementReason" v={d.invalidElementReason} />
          <KV k="invalidElementKeys" v={d.invalidElementKeys} />
          <KV k="firstInvalidElementSummary" v={d.firstInvalidElementSummary} />
          <KV k="totalElementCount" v={d.totalElementCount} />
          <KV k="validElementCount" v={d.validElementCount} />
          <KV k="productImageElementCount" v={d.productImageElementCount} />
          <KV k="textElementCount" v={d.textElementCount} />
          <KV k="logoElementCount" v={d.logoElementCount} />
          <KV k="backgroundOrShapeElementCount" v={d.backgroundOrShapeElementCount} />
          <KV k="audioElementCount" v={d.audioElementCount} />
          <KV k="sourceWrapperUsed" v={d.sourceWrapperUsed} />
          <KV k="integrationVersion" v={d.integrationVersion} />
          {/* TT-CREATOMATE-BRANDED-TEMPLATE-STAGING-BG-REMOVAL-012: template + image-source diagnostics */}
          <KV k="videoTemplateVersion" v={d.videoTemplateVersion} />
          <KV k="categoryTemplateUsed" v={d.categoryTemplateUsed} />
          <KV k="productCategory" v={d.productCategory} />
          <KV k="sceneStructure" v={d.sceneStructure} />
          <KV k="scoreAvailableInVideo" v={d.scoreAvailableInVideo} />
          <KV k="resultText" v={d.resultText} />
          <KV k="videoImageSource" v={d.videoImageSource} />
          <KV k="videoImageSourcePerIndex" v={d.videoImageSourcePerIndex} />
          <KV k="manualCleanAssetUsed" v={d.manualCleanAssetUsed} />
          <KV k="backgroundRemovedImageUsed" v={d.backgroundRemovedImageUsed} />
          <KV k="backgroundRemovalStatus" v={d.backgroundRemovalStatus} />
          <KV k="backgroundRemovalProviderConfigured" v={d.backgroundRemovalProviderConfigured} />
          <KV k="backgroundRemovalWarning" v={d.backgroundRemovalWarning} />
          {/* TT-CREATOMATE-DETERMINISTIC-TEMPLATE-CUTOUT-SYSTEM-015: cutout + template */}
          <KV k="chosenTemplateId" v={d.chosenTemplateId} />
          <KV k="chosenTemplateName" v={d.chosenTemplateName} />
          <KV k="chosenTemplateRequiredMediaType" v={d.chosenTemplateRequiredMediaType} />
          <KV k="productMediaMode" v={d.productMediaMode} />
          <KV k="selectedProductMediaMode" v={d.selectedProductMediaMode} />
          <KV k="cutoutStatus" v={d.cutoutStatus} />
          <KV k="cutoutStatusPerIndex" v={d.cutoutStatusPerIndex} />
          <KV k="cutoutMethod" v={d.cutoutMethod} />
          <KV k="cutoutProvider" v={d.cutoutProvider} />
          <KV k="cutoutProviderConfigured" v={d.cutoutProviderConfigured} />
          <KV k="cutoutNeedsReview" v={d.cutoutNeedsReview} />
          <KV k="originalImageUsed" v={d.originalImageUsed} />
          <KV k="processedCutoutUsed" v={d.processedCutoutUsed} />
          <KV k="useProcessedCutoutForVideo" v={d.useProcessedCutoutForVideo} />
          <KV k="forcedFallbackReason" v={d.forcedFallbackReason} />
          <KV k="cutoutWarnings" v={d.cutoutWarnings} />
          <KV k="persistentScoreEnabled" v={d.persistentScoreEnabled} />
          {/* TT-CREATOMATE-V014-CATEGORY-TEMPLATE-PRESET-SYSTEM: admin selection fields */}
          <KV k="detectedCategory" v={d.detectedCategory} />
          <KV k="selectedVideoCategory" v={d.selectedVideoCategory} />
          <KV k="selectedVideoTemplatePreset" v={d.selectedVideoTemplatePreset} />
          <KV k="selectedProductPlacementMode" v={d.selectedProductPlacementMode} />
          <KV k="selectedBackgroundPreset" v={d.selectedBackgroundPreset} />
          <KV k="concreteBackgroundPreset" v={d.concreteBackgroundPreset} />
          <KV k="selectedSceneSet" v={d.selectedSceneSet} />
          <KV k="categoryDefaultApplied" v={d.categoryDefaultApplied} />
          <KV k="adminTemplateOverrideUsed" v={d.adminTemplateOverrideUsed} />
          <KV k="templateSource" v={d.templateSource} />
          <KV k="creatomateTemplateIdUsed" v={d.creatomateTemplateIdUsed} />
          <KV k="palette" v={d.palette} />
          {/* TT-CREATOMATE-TEMPLATE-V013-FINAL-PRODUCT-VISIBILITY-FIX: visual sanity fields */}
          <KV k="visualSanityPassed" v={d.visualSanityPassed} />
          <KV k="visualSanityWarnings" v={d.visualSanityWarnings} />
          <KV k="productImageElementCount" v={d.productImageElementCount} />
          <KV k="expectedProductSceneCount" v={d.expectedProductSceneCount} />
          <KV k="productImageElementCountMatchesScenes" v={d.productImageElementCountMatchesScenes} />
          <KV k="productImageSourceValid" v={d.productImageSourceValid} />
          <KV k="productImageVisibleByLayout" v={d.productImageVisibleByLayout} />
          <KV k="productImageLayerAboveCard" v={d.productImageLayerAboveCard} />
          <KV k="productImageOpacityValid" v={d.productImageOpacityValid} />
          <KV k="productImageInsideCanvas" v={d.productImageInsideCanvas} />
          <KV k="scoreBadgeElementCount" v={d.scoreBadgeElementCount} />
          <KV k="scoreBadgeOnEveryProductScene" v={d.scoreBadgeOnEveryProductScene} />
          <KV k="largeScoreSceneExists" v={d.largeScoreSceneExists} />
          <KV k="logoReadableByLayout" v={d.logoReadableByLayout} />
          <KV k="introValidByLayout" v={d.introValidByLayout} />
          <KV k="outroValidByLayout" v={d.outroValidByLayout} />
          <KV k="frameCheckAvailable" v={d.frameCheckAvailable} />
          <KV k="frameCheckTimestamps" v={d.frameCheckTimestamps} />
          <KV k="frameCheckPassed" v={d.frameCheckPassed} />
          <KV k="frameCheckWarnings" v={d.frameCheckWarnings} />
          <KV k="creatomateResponseStatus" v={d.creatomateResponseStatus} />
          <KV k="renderStatus" v={d.renderStatus} />
          <KV k="creatomateResultUrlExt" v={d.creatomateResultUrlExt} />
          <KV k="isValidMp4" v={d.isValidMp4} />
          <KV k="detectedWidth" v={d.detectedWidth} />
          <KV k="detectedHeight" v={d.detectedHeight} />
          <KV k="detectedDuration" v={d.detectedDuration} />
          <KV k="invalidReason" v={d.invalidReason} />
          <KV k="safeErrorMessage" v={d.safeErrorMessage} />
        </>
      )}
    </div>
  );
};

export default CreatomateDiagnosticPanel;
